import { MfaPanel, type MfaChallenge } from './MfaPanel';
import { AdministratorInvitation } from './AdministratorInvitation';
import { useEffect, useState } from "react";
import { NewPasswordSchema, PASSWORD_RULES, type InvitationPreview, type User } from "@revival/contracts";
import { SiteIcon } from "./SiteBrand";
import OriginalApp from "./OriginalApp";
import { api } from "./api";
import { clearMediaUrls, isLocalDemo } from "./legacy-api";

const demoUser: User = {
  id: "11111111-1111-4111-8111-111111111111",
  organisationId: "22222222-2222-4222-8222-222222222222",
  contractorCompanyId: null,
  name: "John Lewis",
  email: "demo@revival.local",
  telephone: "",
  role: "administrator",
  active: true,
  mustChangePassword: false,
};

export default function App() {
  const [user, setUser] = useState<User | null>(isLocalDemo() ? demoUser : null);
  const [loading, setLoading] = useState(!isLocalDemo());
  const [error, setError] = useState("");
  const [viewAsUser, setViewAsUser] = useState<User | null>(null);
  const [returnToAdmin, setReturnToAdmin] = useState(false);
  async function startViewAs(userId: string) {
    if (user?.role !== "administrator") return;
    const target = await api.startViewAs(userId);
    clearMediaUrls(); setReturnToAdmin(false); setViewAsUser(target);
  }
  function stopViewAs() {
    api.endViewAs(); clearMediaUrls(); setViewAsUser(null); setReturnToAdmin(true);
  }
  const administratorInvite = new URLSearchParams(window.location.hash.slice(1)).get('administrator-invite') ?? '';
  const inviteToken = new URLSearchParams(window.location.search).get("invite") ?? "";

  useEffect(() => {
    if (isLocalDemo()) return;
    if (!api.isSignedIn()) { setLoading(false); return; }
    void api.me().then(setUser).catch(reason => setError(reason instanceof Error ? reason.message : "Connect to open your saved account.")).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-screen"><div className="brand-mark"><SiteIcon name="snags" /></div><span>Opening Site Snag…</span></div>;
  if (!user && administratorInvite) return <AdministratorInvitation token={administratorInvite} />;
  if (!user && inviteToken) return <InvitationScreen token={inviteToken} onAccepted={setUser} />;
  if (!user) return <LoginScreen initialError={error} onSignedIn={setUser} />;
  if (user.mustChangePassword) return <TemporaryPasswordScreen user={user} onChanged={() => { api.signOut(); setUser(null); setError("Your password has changed. Sign in again with your new password."); }} onSignOut={() => { api.signOut(); setUser(null); }} />;

  return <OriginalApp key={`${user.id}:${viewAsUser?.id ?? 'admin'}:${returnToAdmin}`} user={viewAsUser ?? user} onUserChanged={viewAsUser ? setViewAsUser : setUser} onViewAs={startViewAs} onExitViewAs={viewAsUser ? stopViewAs : undefined} initialAdmin={returnToAdmin} onSignOut={() => {
    if (viewAsUser) { stopViewAs(); return; }
    clearMediaUrls();
    api.signOut();
    setUser(null);
    setError("");
    const url = new URL(window.location.href);
    url.searchParams.delete("invite");
    window.history.replaceState({}, "", url);
  }} />;
}

function TemporaryPasswordScreen({ user, onChanged, onSignOut }: { user: User; onChanged: (user: User) => void; onSignOut: () => void }) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  return <main className="web-auth-page"><section className="web-auth-card">
    <div className="brand-mark auth-brand"><SiteIcon name="snags" /></div>
    <p className="eyebrow">SECURE PASSWORD CHANGE</p>
    <h1>Create a new password</h1>
    <p>Hello {user.name.split(/\s+/)[0]}. Your temporary password worked. Choose your own password before opening any projects.</p>
    <form onSubmit={(event) => {
      event.preventDefault();
      if (!NewPasswordSchema.safeParse(password).success) { setMessage(PASSWORD_RULES); return; }
      if (password !== confirmation) { setMessage("The two passwords do not match"); return; }
      setWorking(true); setMessage("");
      void api.changePassword(password).then(onChanged).catch((reason) => setMessage(reason instanceof Error ? reason.message : "Password could not be changed")).finally(() => setWorking(false));
    }}>
      <label>New password <small>{PASSWORD_RULES}</small><input autoComplete="new-password" minLength={10} required type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} /></label>
      <label>Repeat new password<input autoComplete="new-password" minLength={10} required type={showPassword ? "text" : "password"} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>
      <label className="show-password-control"><input checked={showPassword} onChange={(event) => setShowPassword(event.target.checked)} type="checkbox" /><span>Show passwords</span></label>
      {message && <div className="auth-error">{message}</div>}
      <button className="primary-button" disabled={working || !NewPasswordSchema.safeParse(password).success || password !== confirmation}>{working ? "Saving…" : "Save new password"}</button>
    </form>
    <button className="auth-sign-in-link" onClick={onSignOut} type="button">Sign out</button>
  </section></main>;
}

function LoginScreen({ onSignedIn, initialError }: { onSignedIn: (user: User) => void; initialError: string }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState(initialError);
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  if (challenge) return <main className="web-auth-page"><MfaPanel challenge={challenge} verify={(value, code) => api.completeMfa(value, code)} complete={session => onSignedIn(session.user)} cancel={() => setChallenge(null)} /></main>;
  return <main className="web-auth-page"><section className="web-auth-card">
    <div className="brand-mark auth-brand"><SiteIcon name="snags" /></div>
    <h1>Site Snag</h1><h1 className="auth-title">Sign in</h1>
    <p>Open your secure project workspace on any phone, tablet or computer.</p>
    <form onSubmit={(event) => { event.preventDefault(); setWorking(true); setMessage(""); void api.beginLogin(email, password).then((session) => { if ('mfaRequired' in session) setChallenge(session); else onSignedIn(session.user); }).catch((reason) => setMessage(reason instanceof Error ? reason.message : "Could not sign in")).finally(() => setWorking(false)); }}>
      <label>Email address<input autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} type="email" required /></label>
      <label>Password<input autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} type={showPassword ? "text" : "password"} required /></label>
      <label className="show-password-control"><input checked={showPassword} onChange={(event) => setShowPassword(event.target.checked)} type="checkbox" /><span>Show password</span></label>
      {message && <div className="auth-error">{message}</div>}
      <button className="primary-button" disabled={working}>{working ? "Signing in…" : "Sign in"}</button>
    </form>
    <small>Your organisation controls its projects, photographs, drawings and backups.</small>
  </section></main>;
}

function InvitationScreen({ token, onAccepted }: { token: string; onAccepted: (user: User) => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [checking, setChecking] = useState(true);

  function goToSignIn() {
    const url = new URL(window.location.href);
    url.searchParams.delete("invite");
    window.location.href = url.toString();
  }

  useEffect(() => {
    let active = true;
    void api.previewInvitation(token).then((result) => {
      if (!active) return;
      setPreview(result);
      if (result.kind === "individual" && result.email) setEmail(result.email);
      if (result.kind === "individual" && (result.firstName || result.lastName)) setName(`${result.firstName} ${result.lastName}`.trim());
    }).catch((reason) => {
      if (active) setMessage(reason instanceof Error ? reason.message : "This invitation is unavailable");
    }).finally(() => { if (active) setChecking(false); });
    return () => { active = false; };
  }, [token]);

  if (checking) return <div className="loading-screen"><div className="brand-mark"><SiteIcon name="snags" /></div><span>Checking invitation…</span></div>;

  if (!preview) return <main className="web-auth-page"><section className="web-auth-card invitation-unavailable">
    <div className="brand-mark auth-brand"><SiteIcon name="snags" /></div>
    <p className="eyebrow">INVITATION UNAVAILABLE</p>
    <h1>This link cannot be used</h1>
    <p>{message || "It may have expired or been replaced. Ask the administrator for a new invitation. For contractor access, your site manager can also help."}</p>
    <button className="primary-button" onClick={goToSignIn} type="button">Go to sign in</button>
  </section></main>;

  const companyInvitation = preview.kind === "company";
  const managerInvitation = preview.role === "manager";
  const companyName = preview.company || "your contractor company";
  return <main className="web-auth-page"><section className="web-auth-card">
    <div className="brand-mark auth-brand"><SiteIcon name="snags" /></div>
    <p className="eyebrow">{managerInvitation ? "SITE MANAGER INVITATION" : companyInvitation ? "CONTRACTOR COMPANY INVITATION" : "ONE-TIME INVITATION"}</p>
    <h1>Create your account</h1>
    <p>{companyInvitation
      ? `Set up your own named account for ${companyName}. You will only see snagging assigned to this company.`
      : managerInvitation ? "Set up your own named Site Manager account. This invitation can only be used once."
        : "Set up your own named Site Snag access. This invitation can only be used once."}</p>
    <form onSubmit={(event) => { event.preventDefault(); if (!NewPasswordSchema.safeParse(password).success) { setMessage(PASSWORD_RULES); return; } if (password !== confirmPassword) { setMessage("The two passwords do not match"); return; } setWorking(true); setMessage(""); void api.acceptInvitation({ token, name, email, password }).then((session) => {
      const url = new URL(window.location.href); url.searchParams.delete("invite"); window.history.replaceState({}, "", url); onAccepted(session.user);
    }).catch((reason) => setMessage(reason instanceof Error ? reason.message : "Invitation could not be accepted")).finally(() => setWorking(false)); }}>
      <label>Your name<input autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} required /></label>
      <label>Email address<input autoComplete="email" disabled={Boolean(preview.email)} value={email} onChange={(event) => setEmail(event.target.value)} type="email" required /></label>
      <label>Create password <small>{PASSWORD_RULES}</small><input autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} type={showPassword ? "text" : "password"} minLength={10} required /></label>
      <label>Repeat password<input autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} type={showPassword ? "text" : "password"} minLength={10} required /></label>
      <label className="show-password-control"><input checked={showPassword} onChange={(event) => setShowPassword(event.target.checked)} type="checkbox" /><span>Show passwords</span></label>
      {message && <div className="auth-error">{message}{/already has an account/i.test(message) && <button className="auth-sign-in-link" onClick={goToSignIn} type="button">Sign in instead</button>}</div>}
      <button className="primary-button" disabled={working || !NewPasswordSchema.safeParse(password).success || password !== confirmPassword || !name.trim() || !email.trim()}>{working ? "Creating account…" : "Create my account"}</button>
    </form>
    <button className="auth-sign-in-link" disabled={working} onClick={goToSignIn} type="button">Already have an account? Sign in</button>
    {companyInvitation && <small>This company link may also be used by colleagues. Each person must use their own name, email and password.</small>}
  </section></main>;
}
